import { StateCreator } from "zustand";
import { VendState } from ".";
import { Address, Location, LocationStatusImpact, PausedLocationStatus } from "../types";
import {
  fetchLocations,
  createLocation as createLocationApi,
  editLocation as editLocationApi,
  activateLocation as activateLocationApi,
  putLocationOnHold as putLocationOnHoldApi,
} from "../api/location/api";
import { addLocationContact, listLocationContacts } from "../api/location/contacts";
import { inviteLocationContact } from "../api/user/api";
import { deriveAvailable } from "./machine";
import { hasRequiredContactFields } from "../contactUtils";
import { normalizeIdentityEmail } from "../../utils";
import { useRoutePlannerStore } from "./routePlanner";
import { useUserStore } from "../../user/state/store";
import { createLoadCoordinator, runLoad } from "./helpers";
import { getTaskIdentityKey } from "../taskContext";
import { withWriteAction } from "../utils";
import { taskScopeKeys } from "../taskScopes";
import { resetMachineCollectionSummaryCache } from "../liveSales/store";

const deriveAddresses = (locations: Location[] = []): Address[] =>
  locations
    .map((loc) => loc.address)
    .filter((addr): addr is Address => Boolean(addr));

const replaceLocation = (locations: Location[], updated: Location) => {
  if (!updated.id) return locations;
  let found = false;
  const next = locations.map((location) => {
    if (location.id !== updated.id) return location;
    found = true;
    return updated;
  });
  return found ? next : [...next, updated];
};

const locationsLoads = createLoadCoordinator(() => `${useUserStore.getState().id || ""}:${getTaskIdentityKey()}`);
export const resetLocationLoadState = locationsLoads.reset;

export type LocationSlice = {
  locations: Location[];
  addresses: Address[];
  locationsLoading: boolean;
  locationsError: string | null;
  locationsHydrated: boolean;
  locationsFetchedAt: number | null;
  setLocations: (locations: Location[]) => void;
  addLocation: (location: Location) => void;
  loadLocations: (options?: { force?: boolean }) => Promise<void>;
  createLocation: (location: Location) => Promise<Location>;
  editLocation: (
    location: Location,
    options?: { skipTaskRefresh?: boolean }
  ) => Promise<Location>;
  activateLocation: (locationId: string) => Promise<Location>;
  putLocationOnHold: (locationId: string, impact: LocationStatusImpact, status?: PausedLocationStatus) => Promise<Location>;
};

export const createLocationSlice: StateCreator<
  VendState,
  [],
  [],
  LocationSlice
> = (set, get, _store) => ({
  locations: [],
  addresses: [],
  locationsLoading: false,
  locationsError: null,
  locationsHydrated: false,
  locationsFetchedAt: null,
  setLocations: (locations) => {
    locationsLoads.invalidate();
    set({
      locations,
      addresses: deriveAddresses(locations),
    } as VendState);
  },
  addLocation: (location) => {
    locationsLoads.invalidate();
    const state = get();
    const locations = replaceLocation(state.locations, location);
    set({
      locations,
      addresses: deriveAddresses(locations),
    } as VendState);
  },
  loadLocations: async (options) => {
    if (!options?.force && get().locationsHydrated && !get().locationsError) return;
    return locationsLoads.run(async (current) => {
      const contextCurrent = locationsLoads.capture();
      await runLoad({
        setLoading: (loading) => { if (contextCurrent()) set({ locationsLoading: loading } as VendState); },
        fetcher: fetchLocations,
        setError: (message) => { if (current()) set({ locationsError: message } as VendState); },
        onSuccess: (data) => {
          if (!current()) return;
          set({ locations: data, addresses: deriveAddresses(data), locationsFetchedAt: Date.now() } as VendState);
        },
      });
      if (current()) set({ locationsHydrated: true } as VendState);
    }, Boolean(options?.force));
  },
  createLocation: async (location) => {
    return await withWriteAction(
      "Creating Location",
      async () => {
      const contextCurrent = locationsLoads.capture();
      const created = await createLocationApi(location);
      if (!contextCurrent()) return created;
      const contactDrafts = (location.contacts || []).filter(
        hasRequiredContactFields
      );
      if (created.id && contactDrafts.length) {
        try {
          const outcomes = await Promise.allSettled(
            contactDrafts.map(async (contact) => {
              if (!contextCurrent()) return;
              const payload = {
                firstName: (contact.firstName || "").trim(),
                lastName: (contact.lastName || "").trim(),
                email: normalizeIdentityEmail(contact.email),
                phone: contact.phone || null,
                role: (contact.role as any) || "staff",
              };
              await addLocationContact({
                locationId: created.id || "",
                contact: payload,
              });
              if (!contextCurrent()) return;
              await inviteLocationContact({
                locationId: created.id || "",
                ...payload,
              });
            })
          );
          if (outcomes.some(outcome => outcome.status === "rejected")) {
            created.creationWarnings = ["Some contacts or invitations could not be saved. Open the location to review contacts and resend invitations."];
          }
          if (!contextCurrent()) return created;
          const refreshed = await listLocationContacts(created.id);
          created.contacts = refreshed;
        } catch {
          created.creationWarnings = ["The location was created, but its contacts could not be confirmed. Open the location to review contacts and invitations."];
        }
      }
      if (!contextCurrent()) return created;
      locationsLoads.invalidate();
      const state = get();
      const nextLocations = replaceLocation(
        (get() as VendState).locations,
        created
      );
      set({
        locations: nextLocations,
        addresses: deriveAddresses(nextLocations),
      } as VendState);
      if ((created.machines || []).length > 0) {
        state.loadMachines?.({ force: true }).catch(() => {});
        state.loadTasks?.({ force: true, silent: true,
          filters: { locationId: created.id },
          scopeKey: taskScopeKeys.location(created.id!),
        }).catch(() => {});
      }

      return created;
      }
    );
  },
  editLocation: async (location, options) => {
    return await withWriteAction("Saving Location", async () => {
      const state = get();
      const current = state.locations.find((l) => l.id === location.id);
      if (!current) {
        throw new Error("Location not found");
      }

      const updatedPayload: Location = {
        ...current,
        ...(location as Location),
      };

      const addressChanged =
        updatedPayload.address &&
        JSON.stringify(updatedPayload.address) !==
          JSON.stringify(current.address);
      const contextCurrent = locationsLoads.capture();
      const saved = await editLocationApi(updatedPayload, { includeAddress: Boolean(addressChanged) });
      if (!contextCurrent()) return saved;
      locationsLoads.invalidate();
      const nextLocations = replaceLocation(
        (get() as VendState).locations,
        saved
      );
      set({
        locations: nextLocations,
        addresses: deriveAddresses(nextLocations),
      } as VendState);
      if (addressChanged) {
        resetMachineCollectionSummaryCache(
          Object.keys(saved.machineAssignmentInfo || updatedPayload.machineAssignmentInfo || {})
        );
      }
      if (!options?.skipTaskRefresh && saved.id) {
        state.loadTasks?.({
          force: true,
          silent: true,
          filters: { locationId: saved.id },
          scopeKey: taskScopeKeys.location(saved.id),
        }).catch(() => {});
      } else if (!options?.skipTaskRefresh) {
        state.loadTasks?.({ force: true }).catch(() => {});
      }
      useRoutePlannerStore.getState().reset();
      return saved || updatedPayload;
    });
  },
  activateLocation: async (locationId) => {
    return withWriteAction("Reactivating Location", async () => {
      const contextCurrent = locationsLoads.capture();
      const updated = await activateLocationApi(locationId);
      if (!contextCurrent()) return updated;
      locationsLoads.invalidate();
      const locations = replaceLocation((get() as VendState).locations, updated);
      set({ locations, addresses: deriveAddresses(locations) } as VendState);
      get().loadTasks?.({
        force: true,
        silent: true,
        filters: { locationId },
        scopeKey: taskScopeKeys.location(locationId),
      }).catch(() => {});
      useRoutePlannerStore.getState().reset();
      return updated;
    });
  },
  putLocationOnHold: async (locationId, impact, status = "on_hold") => {
    return withWriteAction(status === "pending_activation" ? "Setting Location Pending Activation" : "Putting Location on Hold", async () => {
      const contextCurrent = locationsLoads.capture();
      const machineIds = new Set(impact.assignedMachines.map((machine) => machine.id));
      const updated = await putLocationOnHoldApi(
        locationId,
        machineIds.size > 0,
        Array.from(machineIds),
        status,
      );
      if (!contextCurrent()) return updated;
      locationsLoads.invalidate();
      const state = get() as VendState;
      const locations = replaceLocation(state.locations, updated);
      const machines = state.machines.map((machine) =>
        machine.id && machineIds.has(machine.id)
          ? { ...machine, assigned: false, location: undefined }
          : machine,
      );
      set({
        locations,
        addresses: deriveAddresses(locations),
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      state.removeTaskSnapshots?.(impact.tasksToDelete.map((task) => task.id));
      useRoutePlannerStore.getState().reset();
      return updated;
    });
  },
});
